import invariant from "tiny-invariant";

import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import PlatformFilterOperator from "@mapmycustomers/shared/enum/PlatformFilterOperator";
import { LegacyFilterCondition } from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import PlatformFilterModel, {
  PlatformFilterCondition,
} from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformFilterModel";
import { isNotEmpty } from "@mapmycustomers/shared/util/assert";

import BaseLocationField from "@app/util/fieldModel/impl/BaseLocationField";
import { isCombinedPlatformCondition, isLegacyCombinedCondition } from "@app/util/viewModel/assert";
import { getRegularFieldConditionValue } from "@app/util/viewModel/convertFromPlatformFilterModel";
import { getRegularFieldPlatformConditionValue } from "@app/util/viewModel/convertToPlatformFilterModel";

import { FieldProperties } from "./Field";

class CheckInLocationField extends BaseLocationField {
  constructor(data: Omit<FieldProperties, "type">) {
    if ("platformAreaFieldName" in data) {
      invariant(
        isNotEmpty(data.platformAreaFieldName),
        "platformAreaFieldName must be defined for CheckInLocationField"
      );
    }

    super({
      features: [
        FieldFeature.CHECK_IN,
        FieldFeature.GEO_POINT_FIELD,
        FieldFeature.FILTERABLE,
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
      ],
      platformAreaFieldName: "geoPointCheckin.coordinates",
      platformFilterNames: ["area", "geoPointCheckin.coordinates"],
      valueGetter: ["geoPointCheckin", "coordinates"],
      ...data,
    });
  }

  convertToPlatformCondition(filterCondition: LegacyFilterCondition): PlatformFilterCondition {
    if (isLegacyCombinedCondition(filterCondition)) {
      return {}; // combined condition is not supported
    } else {
      if (
        filterCondition.operator === FilterOperator.EMPTY ||
        filterCondition.operator === FilterOperator.NOT_EMPTY
      ) {
        return {
          [this._platformAreaFieldName!]: {
            [filterCondition.operator === FilterOperator.EMPTY
              ? PlatformFilterOperator.EQUALS
              : PlatformFilterOperator.NOT_EQUAL]: null,
          },
        };
      } else {
        return {
          [this._platformFilterNames[0]]: getRegularFieldPlatformConditionValue(
            this,
            filterCondition
          ),
        };
      }
    }
  }

  convertFromPlatformCondition(
    filterCondition: PlatformFilterCondition
  ): LegacyFilterCondition | undefined {
    if (isCombinedPlatformCondition(filterCondition)) {
      return super.convertFromPlatformCondition(filterCondition);
    } else {
      if (this._platformAreaFieldName! in filterCondition) {
        const keys = Object.keys(filterCondition[this._platformAreaFieldName!] as object);
        if (!keys.length) {
          return undefined;
        }
        return {
          operator: keys.includes(PlatformFilterOperator.EQUALS)
            ? FilterOperator.EMPTY
            : FilterOperator.NOT_EMPTY,
          value: undefined,
        };
      }

      return getRegularFieldConditionValue(this, filterCondition);
    }
  }

  isPresentInPlatformCondition(condition: Record<string, unknown>): boolean {
    return (
      super.isPresentInPlatformCondition(condition) || this._platformAreaFieldName! in condition
    );
  }

  getFilterConditionForMetaField(
    platformFilterModel: PlatformFilterModel
  ): PlatformFilterCondition {
    if (this.platformFilterNames[0] in platformFilterModel) {
      return super.getFilterConditionForMetaField(platformFilterModel);
    } else if (this._platformAreaFieldName! in platformFilterModel) {
      // we know [this._platformAreaFieldName] field can be in PlatformFilterModel because we put it there ourselves
      // @ts-ignore
      return { [this._platformAreaFieldName!]: platformFilterModel[this._platformAreaFieldName!] };
    }
    invariant(false, `Invalid filter condition for ${this.name} field.`);
  }
}

export default CheckInLocationField;
